import React, { useState, useEffect } from 'react';
import { contentAPI, teamsAPI } from '../../services/api';
import { FaUserEdit, FaTrash, FaPlus, FaFilter, FaNewspaper, FaImage, FaEye } from 'react-icons/fa';
import { useLanguage } from '../../context/LanguageContext';

const ClubNewsSection = () => {
  const { language, t } = useLanguage();
  const [clubNews, setClubNews] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTeamFilter, setSelectedTeamFilter] = useState('all');

  const [formData, setFormData] = useState({
    team_id: '',
    title: '',
    title_ar: '',
    content: '',
    content_ar: '',
    category: 'general',
    image: null
  });
  const [editingId, setEditingId] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [newsRes, teamsRes] = await Promise.all([
        contentAPI.getClubNews(),
        teamsAPI.getAll()
      ]);

      setClubNews(newsRes.data || []);
      setTeams(teamsRes.data || []);
    } catch (error) {
      console.error('Erreur chargement:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (news) => {
    setEditingId(news.id);
    setFormData({
      team_id: news.team_id,
      title: news.title || '',
      title_ar: news.title_ar || '',
      content: news.content || '',
      content_ar: news.content_ar || '',
      category: news.category || 'general',
      image: null
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = new FormData();
      data.append('team_id', formData.team_id);
      data.append('title', formData.title);
      data.append('title_ar', formData.title_ar);
      data.append('content', formData.content);
      data.append('content_ar', formData.content_ar);
      data.append('category', formData.category);
      if (formData.image) data.append('image', formData.image);

      if (editingId) {
        await contentAPI.updateClubNews(editingId, data);
      } else {
        await contentAPI.createClubNews(data);
      }

      resetForm();
      loadData();
      alert(language === 'ar' ? 'تم حفظ الخبر بنجاح!' : 'Actualité enregistrée avec succès !');
    } catch (error) {
      alert('Erreur: ' + error.message);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm(language === 'ar' ? 'هل أنت متأكد من حذف هذا الخبر؟' : 'Supprimer cette actualité ?')) {
      try {
        await contentAPI.deleteClubNews(id);
        loadData();
      } catch (error) {
        alert('Erreur: ' + error.message);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      team_id: '',
      title: '',
      title_ar: '',
      content: '',
      content_ar: '',
      category: 'general',
      image: null
    });
    setEditingId(null);
    setShowModal(false);
  };

  const getTeamName = (teamId) => {
    const team = teams.find(t => t.id === teamId);
    return team ? team.name : 'Unknown';
  };

  const filteredNews = selectedTeamFilter === 'all'
    ? clubNews
    : clubNews.filter(n => n.team_id === parseInt(selectedTeamFilter));

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner}></div>
        <p>{t('loading')}</p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>📰 {language === 'ar' ? 'أخبار الأندية' : 'Actualités des Clubs'}</h1>
        <button onClick={() => setShowModal(true)} style={styles.addBtn}>
          <FaPlus style={{ [language === 'ar' ? 'marginLeft' : 'marginRight']: '8px' }} />
          {language === 'ar' ? 'خبر جديد' : 'Nouvelle Actualité'}
        </button>
      </div>

      <div style={styles.filterSection}>
        <div style={styles.filterGroup}>
          <FaFilter style={styles.filterIcon} />
          <select
            value={selectedTeamFilter}
            onChange={(e) => setSelectedTeamFilter(e.target.value)}
            style={styles.select}
          >
            <option value="all">{language === 'ar' ? 'كل الأندية' : 'Toutes les équipes'}</option>
            {teams.map(team => (
              <option key={team.id} value={team.id}>{team.name}</option>
            ))}
          </select>
        </div>
        <div style={styles.stats}>{filteredNews.length} {language === 'ar' ? 'خبر' : 'Actualités'}</div>
      </div>

      <div style={styles.newsGrid}>
        {filteredNews.map(news => (
          <div key={news.id} style={styles.card}>
            <div style={styles.cardImage}>
              {news.image_url ? (
                <img src={news.image_url} alt="" style={styles.img} />
              ) : (
                <div style={styles.imgPlaceholder}><FaImage size={40} /></div>
              )}
              <div style={styles.categoryBadge}>{news.category}</div>
            </div>
            <div style={styles.cardContent}>
              <div style={styles.teamName}>{getTeamName(news.team_id)}</div>
              <h3 style={{ ...styles.newsTitle, direction: language === 'ar' ? 'rtl' : 'ltr', textAlign: language === 'ar' ? 'right' : 'left' }}>
                {language === 'ar' ? (news.title_ar || news.title) : news.title}
              </h3>
              <p style={{ ...styles.newsExcerpt, direction: language === 'ar' ? 'rtl' : 'ltr', textAlign: language === 'ar' ? 'right' : 'left' }}>
                {language === 'ar' ?
                  (news.content_ar?.substring(0, 100) || news.content?.substring(0, 100)) :
                  (news.content?.substring(0, 100) || news.content_ar?.substring(0, 100))}...
              </p>
              <div style={styles.cardFooter}>
                <div style={styles.meta}><FaEye size={12} /> {news.views || 0}</div>
                <div style={styles.actions}>
                  <button onClick={() => handleEdit(news)} style={styles.editBtn}><FaUserEdit /></button>
                  <button onClick={() => handleDelete(news.id)} style={styles.deleteBtn}><FaTrash /></button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {showModal && (
        <div style={styles.overlay}>
          <div style={styles.modal}>
            <h2 style={styles.modalTitle}>
              {editingId ? (language === 'ar' ? 'تعديل الخبر' : 'Modifier l\'actualité') : (language === 'ar' ? 'إضافة خبر' : 'Ajouter une actualité')}
            </h2>
            <form onSubmit={handleSubmit} style={styles.form}>
              <div style={styles.formGroupModal}>
                <label style={styles.label}>{language === 'ar' ? 'النادي' : 'Équipe'}</label>
                <select
                  value={formData.team_id}
                  onChange={e => setFormData({ ...formData, team_id: e.target.value })}
                  style={styles.modalInput}
                  required
                >
                  <option value="">{language === 'ar' ? 'اختر النادي' : 'Choisir une équipe'}</option>
                  {teams.map(team => (
                    <option key={team.id} value={team.id}>{team.name}</option>
                  ))}
                </select>
              </div>

              <div style={styles.formRow}>
                <div style={styles.formCol}>
                  <label style={styles.label}>Titre (FR)</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={e => setFormData({ ...formData, title: e.target.value })}
                    style={styles.modalInput}
                    required
                  />
                </div>
                <div style={styles.formCol}>
                  <label style={styles.label}>العنوان (AR)</label>
                  <input
                    type="text"
                    value={formData.title_ar}
                    onChange={e => setFormData({ ...formData, title_ar: e.target.value })}
                    style={{ ...styles.modalInput, direction: 'rtl' }}
                    required
                  />
                </div>
              </div>

              <div style={styles.formRow}>
                <div style={styles.formCol}>
                  <label style={styles.label}>Contenu (FR)</label>
                  <textarea
                    value={formData.content}
                    onChange={e => setFormData({ ...formData, content: e.target.value })}
                    style={{ ...styles.modalInput, height: '100px' }}
                    required
                  />
                </div>
                <div style={styles.formCol}>
                  <label style={styles.label}>المحتوى (AR)</label>
                  <textarea
                    value={formData.content_ar}
                    onChange={e => setFormData({ ...formData, content_ar: e.target.value })}
                    style={{ ...styles.modalInput, height: '100px', direction: 'rtl' }}
                    required
                  />
                </div>
              </div>

              <div style={styles.formRow}>
                <div style={styles.formCol}>
                  <label style={styles.label}>{language === 'ar' ? 'الصنف' : 'Catégorie'}</label>
                  <select
                    value={formData.category}
                    onChange={e => setFormData({ ...formData, category: e.target.value })}
                    style={styles.modalInput}
                  >
                    <option value="general">General</option>
                    <option value="match">Match</option>
                    <option value="transfer">Transfert</option>
                    <option value="training">Entraînement</option>
                  </select>
                </div>
                <div style={styles.formCol}>
                  <label style={styles.label}>{language === 'ar' ? 'الصورة' : 'Image'}</label>
                  <input
                    type="file"
                    onChange={e => setFormData({ ...formData, image: e.target.files[0] })}
                    style={styles.modalInput}
                  />
                </div>
              </div>

              <div style={styles.modalActions}>
                <button type="submit" style={styles.saveBtn}>{t('save')}</button>
                <button type="button" onClick={resetForm} style={styles.cancelBtn}>{t('cancel')}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: { padding: '20px' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' },
  title: { fontSize: '24px', fontWeight: 'bold', color: '#1a365d' },
  addBtn: { display: 'flex', alignItems: 'center', padding: '10px 20px', background: '#28a745', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' },
  filterSection: { display: 'flex', alignItems: 'center', gap: '20px', marginBottom: '20px', background: 'white', padding: '15px', borderRadius: '12px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' },
  filterGroup: { display: 'flex', alignItems: 'center', gap: '10px', flex: 1 },
  filterIcon: { color: '#64748b' },
  select: { padding: '8px 12px', borderRadius: '8px', border: '1px solid #e2e8f0', outline: 'none', width: '250px' },
  stats: { fontWeight: 'bold', color: '#64748b' },
  newsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '25px' },
  card: { background: 'white', borderRadius: '16px', overflow: 'hidden', boxShadow: '0 4px 6px rgba(0,0,0,0.05)', transition: 'transform 0.2s', border: '1px solid #f1f5f9' },
  cardImage: { height: '180px', position: 'relative', background: '#f8fafc' },
  img: { width: '100%', height: '100%', objectFit: 'cover' },
  imgPlaceholder: { width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#cbd5e1' },
  categoryBadge: { position: 'absolute', top: '10px', right: '10px', background: 'rgba(26, 54, 93, 0.9)', color: 'white', padding: '4px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 'bold' },
  cardContent: { padding: '20px' },
  teamName: { fontSize: '12px', fontWeight: 'bold', color: '#c41e3a', marginBottom: '8px', textTransform: 'uppercase' },
  newsTitle: { fontSize: '18px', fontWeight: 'bold', color: '#1e293b', marginBottom: '10px', lineHeight: '1.4' },
  newsExcerpt: { fontSize: '14px', color: '#64748b', marginBottom: '15px', lineHeight: '1.6' },
  cardFooter: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid #f1f5f9', paddingTop: '15px' },
  meta: { fontSize: '12px', color: '#94a3b8', display: 'flex', alignItems: 'center', gap: '5px' },
  actions: { display: 'flex', gap: '8px' },
  editBtn: { padding: '8px', background: '#e0f2fe', color: '#0369a1', border: 'none', borderRadius: '6px', cursor: 'pointer' },
  deleteBtn: { padding: '8px', background: '#fee2e2', color: '#b91c1c', border: 'none', borderRadius: '6px', cursor: 'pointer' },
  overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(4px)' },
  modal: { background: 'white', padding: '30px', borderRadius: '20px', width: '90%', maxWidth: '800px', maxHeight: '90vh', overflowY: 'auto' },
  modalTitle: { fontSize: '20px', fontWeight: 'bold', marginBottom: '25px', color: '#1e293b' },
  form: { display: 'flex', flexDirection: 'column', gap: '20px' },
  formGroupModal: { display: 'flex', flexDirection: 'column', gap: '8px' },
  formRow: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' },
  formCol: { display: 'flex', flexDirection: 'column', gap: '8px' },
  label: { fontSize: '13px', fontWeight: 'bold', color: '#64748b' },
  modalInput: { padding: '12px', border: '1px solid #e2e8f0', borderRadius: '10px', outline: 'none', fontSize: '14px' },
  modalActions: { display: 'flex', gap: '15px', marginTop: '10px' },
  saveBtn: { flex: 1, padding: '12px', background: '#1a365d', color: 'white', border: 'none', borderRadius: '10px', fontWeight: 'bold', cursor: 'pointer' },
  cancelBtn: { flex: 1, padding: '12px', background: '#f1f5f9', color: '#64748b', border: 'none', borderRadius: '10px', fontWeight: 'bold', cursor: 'pointer' },
  loadingContainer: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '100px' },
  spinner: { width: '40px', height: '40px', border: '4px solid #f3f3f3', borderTop: '4px solid #c41e3a', borderRadius: '50%', animation: 'spin 1s linear infinite' }
};

export default ClubNewsSection;
